import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { doc, updateDoc, serverTimestamp, increment } from 'firebase/firestore';
import { db } from '../config/firebaseConfig';
import { HEARTBEAT_INTERVAL_MS } from '../utils/presence';
import { accumulateDailyActivity, markDailySession, MAX_FLUSH_MS } from '../utils/onlineActivity';

// Last presence heartbeat, at module scope: the screens running the heartbeat
// (TopBar on every Layout page, and the Hall) remount on every route change and
// the throttle must survive that.
let lastHeartbeatAt = 0;

// `pagehide` yields a logoutTime stamp on tab close. If the page comes back
// (reload, or bfcache restore), the first heartbeat has to clear that stamp or
// the user would look offline until the next tick. Module scope resets on a
// real page load, which is exactly the lifetime we want.
let hasClearedLogoutThisPageLoad = false;

// Nabızla birlikte yazılan son rota. Modül kapsamında tutulur çünkü nabzı atan
// ekranlar her rota değişiminde yeniden mount oluyor; aynı yolu tekrar tekrar
// yazmamak için bu değerin remount'lara dayanması gerekiyor.
let lastWrittenPath = null;

const sessionKeyFor = (employeeId) => `hb_session_started_${employeeId}`;

/**
 * Presence heartbeat. The calling screen remounts on every route change, so
 * this deliberately does NOT write logoutTime on unmount: doing so raced the
 * next mount's login write and could flip an actively-navigating user offline.
 * Instead the client refreshes `lastSeen` on a timer and the reader treats a
 * stale stamp as offline. See utils/presence.js.
 *
 * Two layers mark a user offline, in order of speed:
 *   1. `pagehide` writes logoutTime on tab/browser close — instant, but the
 *      browser may kill the request before it lands, so it is best-effort.
 *   2. The heartbeat going stale past ONLINE_TTL_MS — always works, including
 *      after a crash, a force-quit or a dropped connection. This is the
 *      guarantee; layer 1 is only an optimisation on top of it.
 *
 * sessionStartTime/sessionCount/loginTime are written once per browser-tab
 * session via a sessionStorage flag. totalOnlineMs accumulates elapsed time
 * for usage statistics.
 */
const usePresenceHeartbeat = (employeeId) => {
  const location = useLocation();
  // Nabız fonksiyonu efekt içinde kapanış (closure) olarak yaşıyor; güncel
  // rotayı ref üzerinden okur ki efektin yeniden kurulması gerekmesin.
  const currentPathRef = useRef(location.pathname);

  useEffect(() => {
    if (!employeeId) return;
    const employeeRef = doc(db, 'Employees', employeeId);
    const sessionKey = sessionKeyFor(employeeId);
    let lastFlush = Date.now();

    const sendHeartbeat = async ({ force = false } = {}) => {
      const now = Date.now();
      // Rota değiştiğinde nabız kısıtı atlanır: "şu an hangi sayfada" bilgisi
      // ancak bu şekilde anlık kalır. Rota değişimi kullanıcı hızında olduğu
      // için bu ekstra yazma sayısını anlamlı biçimde artırmaz.
      const pathChanged = currentPathRef.current !== lastWrittenPath;
      // Throttled at module scope so a burst of route changes does not turn
      // into a burst of writes.
      if (!force && !pathChanged && now - lastHeartbeatAt < HEARTBEAT_INTERVAL_MS) return;
      lastHeartbeatAt = now;

      try {
        const updates = { lastSeen: serverTimestamp() };
        // Sayfa Yetkileri ekranındaki "Çevrimiçi Kullanıcılar" listesi bu iki
        // alanı okuyup kullanıcının o an hangi menüde olduğunu gösterir.
        if (pathChanged) {
          lastWrittenPath = currentPathRef.current;
          updates.currentPath = currentPathRef.current;
          updates.currentPathAt = serverTimestamp();
        }
        if (!hasClearedLogoutThisPageLoad) {
          hasClearedLogoutThisPageLoad = true;
          // Sekme kapanışında yazılmış olabilecek çıkış damgasını sil. Sayfa
          // yenilendiğinde sessionStorage bayrağı durduğu için aşağıdaki
          // "yeni oturum" bloğu çalışmaz; temizlik bu yüzden ayrı.
          updates.logoutTime = null;
        }
        if (!sessionStorage.getItem(sessionKey)) {
          sessionStorage.setItem(sessionKey, String(now));
          updates.loginTime = serverTimestamp();
          updates.sessionStartTime = serverTimestamp();
          updates.sessionCount = increment(1);
          // Ömür boyu sayacın gün bazlı karşılığı; ekip raporları bunu okur.
          markDailySession(employeeId, now);
        }
        await updateDoc(employeeRef, updates);
      } catch (error) {
        console.debug('Failed to send presence heartbeat:', error);
      }
    };

    const flushOnlineTime = () => {
      const now = Date.now();
      const rawElapsed = now - lastFlush;
      lastFlush = now;
      if (rawElapsed < 1000) return;
      // Uyku modunda zamanlayıcı durur; uyanışta arada saatler görünebilir.
      // Gerçekte sistemde geçirilmemiş bu süre toplamları şişirmesin diye
      // tek seferde eklenebilecek süre kırpılır.
      const elapsed = Math.min(rawElapsed, MAX_FLUSH_MS);
      updateDoc(employeeRef, { totalOnlineMs: increment(elapsed) }).catch((error) => {
        console.debug('Failed to flush online time:', error);
      });
      // Aynı süre bir de güne bölünmüş olarak yazılır; ömür boyu sayaç tarih
      // aralığına göre raporlanamadığı için (bkz. utils/onlineActivity.js).
      accumulateDailyActivity(employeeId, now - elapsed, now);
    };

    sendHeartbeat();
    const heartbeatInterval = setInterval(() => sendHeartbeat({ force: true }), HEARTBEAT_INTERVAL_MS);
    const flushInterval = setInterval(flushOnlineTime, 5 * 60 * 1000);

    // Coming back to the tab should refresh presence immediately rather than
    // waiting out the remainder of the interval.
    const handleVisibilityChange = () => {
      if (!document.hidden) sendHeartbeat();
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Sekme/tarayıcı kapanışında anında çevrimdışı görünmek için son bir deneme.
    // `pagehide`, `beforeunload`'ın aksine mobilde de tetiklenir ve YALNIZCA
    // gerçek sayfa yıkımında çalışır — React rota değişimlerinde çalışmadığı
    // için eskiden yaşanan "gezinirken çevrimdışı düşme" yarışı geri gelmez.
    // Yazının tamamlanacağı garanti değildir; garanti eden şey nabız TTL'idir.
    const handlePageHide = () => {
      lastHeartbeatAt = 0;
      hasClearedLogoutThisPageLoad = false;
      updateDoc(employeeRef, { logoutTime: serverTimestamp() }).catch(() => {});
    };
    window.addEventListener('pagehide', handlePageHide);

    // Geri/ileri ile bfcache'ten dönen sayfa yeni bir yükleme sayılmaz; nabzı
    // elle tazelemezsek kullanıcı çevrimdışı görünmeye devam eder.
    const handlePageShow = (event) => {
      if (event.persisted) sendHeartbeat({ force: true });
    };
    window.addEventListener('pageshow', handlePageShow);

    return () => {
      clearInterval(heartbeatInterval);
      clearInterval(flushInterval);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('pagehide', handlePageHide);
      window.removeEventListener('pageshow', handlePageShow);
      flushOnlineTime();
    };
  }, [employeeId]);

  // Rota her değiştiğinde nabzı tazele; böylece `currentPath` anlık kalır.
  useEffect(() => {
    currentPathRef.current = location.pathname;
    if (!employeeId) return;
    if (location.pathname === lastWrittenPath) return;
    lastWrittenPath = location.pathname;
    updateDoc(doc(db, 'Employees', employeeId), {
      lastSeen: serverTimestamp(),
      currentPath: location.pathname,
      currentPathAt: serverTimestamp(),
    }).catch((error) => {
      console.debug('Failed to write current path:', error);
    });
  }, [employeeId, location.pathname]);
};

/**
 * Explicit sign-out ("Çıkış Yap"): mark the user offline right away instead of
 * waiting for the heartbeat to go stale, and make the next login a new session.
 */
export const endPresenceSession = async (employeeId) => {
  if (!employeeId) return;
  // Yeni girişin yeni oturum sayılması için oturum bayrağını temizle
  sessionStorage.removeItem(sessionKeyFor(employeeId));
  // Açık çıkışta nabzın eskimesini beklemeden çevrimdışı görünsün.
  // Nabız atmayı da durdur ki sonraki mount hemen yeniden yazmasın.
  lastHeartbeatAt = Date.now();
  try {
    await updateDoc(doc(db, 'Employees', employeeId), {
      logoutTime: serverTimestamp(),
      lastSeen: null
    });
  } catch (error) {
    console.debug('Failed to clear presence on logout:', error);
  }
};

export default usePresenceHeartbeat;
